import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Cropper from 'react-easy-crop';
import { getOrientation } from 'get-orientation/browser';
import { getCroppedImg, getRotatedImage, convertImageToBlob, readFile } from './image-utils/canvasUtils';
import { Rotate2, RotateClockwise2 } from 'tabler-icons-react';
import Button from 'components/elements/buttons/Button';
import CommonModal from 'components/elements/modals/CommonModal';
import CommonDropzone from 'components/common/CommonDropzone';
import RangeSlider from 'components/elements/RangeSlider';

import './CommonImageDropzone.css';

const ORIENTATION_TO_ANGLE = {
  '3': 180,
  '6': 90,
  '8': -90,
};

const CommonImageDropzone = (props) => {
  const {
    children,
    multiple, // optional
    modalClassName, // optional
    onDropImage,
    onLoadSuccess, // optional
    onFailure, // optional
    imageZoom, // optional
    aspectRatio, // optional
  } = props;
  const [ imageSrc, setImageSrc ] = useState(null); // set icons on drop
  const [ imageIndex, setImageIndex ] = useState(0); // set image index for multiple prop
  const [ error, setError ] = useState(null); // set image errors
  const [ crop, setCrop ] = useState({ x: 0, y: 0 });
  const [ rotation, setRotation ] = useState(0);
  const [ zoom, setZoom ] = useState(1);
  const [ croppedAreaPixels, setCroppedAreaPixels ] = useState([]);
  const [ croppedImage, setCroppedImage ] = useState([]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixelsCurrent) => {
    setCroppedAreaPixels((croppedAreaPixels) => ({
      ...croppedAreaPixels,
      [imageIndex || 0]: croppedAreaPixelsCurrent,
    }));
  }, [ imageSrc, croppedAreaPixels, imageIndex ]);

  const handleRotateClick = (rotatedTo, value = 90) => {
    let initialRotate = 0;
    const rotationValue = rotation[imageIndex] || 0;
    switch (rotatedTo) {
      case 'left':
        initialRotate = rotationValue - value;
        break;
      case 'right':
        initialRotate = rotationValue + value;
        break;
      default:
        initialRotate = 0;
    }

    if (initialRotate < 0) {
      initialRotate = 360 - value;
    }
    if (initialRotate > 360) {
      initialRotate = 0 + value;
    }

    setRotation((stateRotate) => ({
      ...stateRotate,
      [imageIndex || 0]: initialRotate,
    }));
  };

  const handleSave = useCallback(async () => {
    const array = [];

    if (multiple) {
      for (let i = 0; i < imageSrc.length; i += 1) {
        try {
          const croppedCurrentImage = await getCroppedImg(
            imageSrc[i].image,
            croppedAreaPixels[i],
            rotation[i] ? rotation[i] : 0,
            imageSrc[i].type,
          );
          const convertImageToBlobData = await convertImageToBlob(croppedCurrentImage);

          array.push({
            image: croppedCurrentImage,
            imageBlob: convertImageToBlobData, // needed to patch to backend
            name: imageSrc[i].name,
            type: imageSrc[i].type,
          });
        } catch (e) {
          console.error(e);
        }
      }

      setCroppedImage(array);
      onDropImage(array);
      setImageIndex(0);

    } else {
      try {
        const croppedCurrentImage = await getCroppedImg(
          imageSrc[0].image,
          croppedAreaPixels[0],
          rotation[0] ? rotation[0] : 0,
          imageSrc[0].type,
        );
        const convertImageToBlobData = await convertImageToBlob(croppedCurrentImage);

        array.push({
          image: croppedCurrentImage,
          imageBlob: convertImageToBlobData, // needed to patch to backend
          name: imageSrc[0].name,
          type: imageSrc[0].type,
        });
      } catch (e) {
        console.error(e);
      }

      setCroppedImage(array[0]);
      onDropImage(array[0]);
    }

    setCroppedImage(null);
    setImageSrc(null);

  }, [ imageSrc, croppedAreaPixels, rotation ]);

  const onClose = useCallback(() => {
    setError(null);
    setCroppedImage(null);
    setImageSrc(null);
    setImageIndex(0);
  }, []);

  const handleChangeImage = useCallback(() => {
    setImageIndex((index) => index + 1);
  }, []);

  const onFileChange = async items => {
    if (items && items.length > 0) {
      if (onLoadSuccess) {
        onLoadSuccess();
      }
      setError(null);
      const files = items.map(i => i);
      const obj = [];

      if (files.length === 1 && files[0].type === 'image/gif') {
        let imageDataUrl = await readFile(files[0]);
        const imageBlob = await convertImageToBlob(imageDataUrl);
        
        onDropImage(
          {image: imageDataUrl,
          imageBlob: imageBlob, // needed to patch to backend
          name: files[0].name,
          type: files[0].type,})
        return;
      }

      for (let i = 0; i < files.length; i += 1) {
        let imageDataUrl = await readFile(files[i]);

        // apply rotation if needed
        const orientation = await getOrientation(files[i]);
        const rotation = ORIENTATION_TO_ANGLE[orientation];

        if (rotation) {
          imageDataUrl = await getRotatedImage(imageDataUrl, rotation, files[i].type);
        }

        obj.push({
          image: imageDataUrl,
          orientation,
          rotation,
          name: files[i].name,
          path: files[i].path,
          type: files[i].type,
        });
      }
      setImageSrc(obj);
    }
  };

  const handleImageError = errorImage => {
    if (errorImage) {
      if (onFailure) {
        onFailure(errorImage);
      }
      setError(errorImage);
    }
  };

  // TODO
  // add rotation and zoom handling for each icons when props.multiple is true

  return (
    <Fragment>
      {imageSrc || error ? (
        <Fragment>
          <CommonModal
            className={`common-image-dropzone-modal__component ${modalClassName} ${error ? 'error' : ''}`}
            open={Boolean(imageSrc) || Boolean(error)}
            onClose={onClose}
            // size="large"
          >
            <div className="wrapper">
              <div className="text-content">
                {error &&
                  <>
                    {error && error.length > 0 && error.map(i => (
                      <p
                        key={`common_image_dropzone_error_item_${i.code}`}
                        className="text-content-error-item"
                      >
                        {i.text}
                      </p>
                    ))}
                  </>
                }
                {error ?
                  <CommonDropzone
                    {...props}
                    onDrop={onFileChange}
                    onError={handleImageError}
                  >
                    <div className="common-image-dropzone-drop-section">
                      <div className="common-image-dropzone-drop-section-wrapper">
                        <div className="icon" />
                        <p><b>Drag and drop or click here</b><br /> to upload your image (max {error[0].maxSize})</p>
                      </div>
                    </div>
                  </CommonDropzone>
                  :
                  <Cropper
                    image={imageSrc[imageIndex].image}
                    crop={crop}
                    rotation={rotation[imageIndex] || 0}
                    zoom={zoom}
                    aspect={aspectRatio}
                    onCropChange={setCrop}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                }
              </div>

              {!error &&
                <div className="buttons-block">
                  <div className="common-image-dropzone-controls">
                    <div className="common-image-dropzone-controls-wrapper">
                      <div
                        className="rotate-control left"
                        role="button"
                        tabIndex={-1}
                        onClick={() => { handleRotateClick('left'); }}
                      >
                        <Rotate2 />
                      </div>
                      <RangeSlider
                        className="common-image-dropzone-slider__slider"
                        min={imageZoom.min}
                        max={imageZoom.max}
                        defaultValue={imageZoom.defaultValue}
                        step={imageZoom.step}
                        onChange={(value) => {
                          setZoom(value);
                        }}
                        value={zoom}
                      />
                      <div
                        className="rotate-control right"
                        role="button"
                        tabIndex={-1}
                        onClick={() => { handleRotateClick('right'); }}
                      >
                        <RotateClockwise2 />
                      </div>
                    </div>
                    {imageSrc && imageSrc.length > 0 && imageIndex !== imageSrc.length - 1 ?
                      <Button
                        className="modal-button confirm"
                        onClick={handleChangeImage}
                      >
                        Next
                      </Button>
                      :
                      <Button
                        className="modal-button confirm"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    }

                  </div>
                </div>
              }
            </div>
          </CommonModal>
        </Fragment>
      ) : (
        <CommonDropzone
          {...props}
          onDrop={onFileChange}
          onError={handleImageError}
        >
          {children}
        </CommonDropzone>
      )}
    </Fragment>
  );
};

CommonImageDropzone.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.element, PropTypes.string]).isRequired,
  modalClassName: PropTypes.string,
  multiple: PropTypes.bool,
  imageZoom: PropTypes.object,
  onDropImage: PropTypes.func.isRequired,
  onLoadSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  aspectRatio: PropTypes.number,
};
CommonImageDropzone.defaultProps = {
  modalClassName: '',
  multiple: false,
  imageZoom: { min: 1, max: 3, step: 0.01, defaultValue: 1, currentValue: 1 },
  onLoadSuccess: null,
  onFailure: null,
  aspectRatio: 1 / 1,
};

export default CommonImageDropzone;
